import { Fragment, useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, ChevronDown, ChevronRight, Copy, Cpu, Eye, EyeOff, Plus, RefreshCw, Server, Terminal, Trash2, Unplug } from "lucide-react";
import { Shell } from "@/components/Shell";
import { EaSetup, eaEndpoint } from "@/components/EaSetup";
import { EaHealth } from "@/components/EaHealth";
import { EaDownload } from "@/components/EaDownload";
import { api, errMsg } from "@/lib/api";
import { Masked } from "@/components/Masked";
import { CollapsibleCard } from "@/components/CollapsibleCard";
import { MobileRow } from "@/components/MobileRow";

const card =
  "border border-zinc-800 bg-zinc-950/60 rounded-sm p-4 sm:p-5 space-y-4";
const inputCls =
  "w-full mt-1 rounded-sm bg-zinc-900/60 border border-zinc-800 px-3 py-2 font-mono text-sm text-zinc-100 focus:border-blue-500 focus:outline-none transition-colors duration-200";
const label = "text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-semibold";
const th = "text-left px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-zinc-500";
const td = "px-3 py-2 font-mono text-xs text-zinc-300 border-t border-zinc-900";

const Label = ({ children }) => <span className={label}>{children}</span>;

const maskSecret = (s) => (s ? `${s.slice(0, 4)}${"•".repeat(8)}${s.slice(-4)}` : "—");

const EaDot = ({ online, assigned }) => (
  <span
    className={`inline-flex items-center gap-1.5 font-mono text-[11px] ${
      !assigned ? "text-zinc-600" : online ? "text-emerald-400" : "text-red-400"
    }`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${
        !assigned ? "bg-zinc-700" : online ? "bg-emerald-400 animate-pulse" : "bg-red-500"
      }`}
    />
    {!assigned ? "free" : online ? "online" : "offline"}
  </span>
);

export default function Pods() {
  const [pods, setPods] = useState([]);
  const [cap, setCap] = useState(null);
  const [traders, setTraders] = useState([]);
  const [slots, setSlots] = useState([]);
  const [pick, setPick] = useState({});
  const [form, setForm] = useState({
    name: "",
    max_users: 5,
    port_start: 7701,
    region: "",
  });
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState({});
  const [copied, setCopied] = useState(null);
  const [eaOpen, setEaOpen] = useState({});

  const copyValue = async (text, key, msg) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      toast.success(msg);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error("Clipboard blocked — copy it manually");
    }
  };

  const load = useCallback(async () => {
    try {
      const [p, c, u, s] = await Promise.all([
        api.get("/admin/pods"),
        api.get("/admin/capacity"),
        api.get("/admin/users"),
        api.get("/admin/slots"),
      ]);
      setPods(p.data);
      setCap(c.data);
      setTraders(u.data.filter((x) => x.role !== "admin"));
      setSlots(s.data.slots || []);
    } catch (e) {
      toast.error(errMsg(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rotateSecret = async (slot) => {
    try {
      const { data } = await api.post(`/admin/slots/${slot.slot_id}/rotate-secret`);
      setRevealed((r) => ({ ...r, [slot.slot_id]: true }));
      toast.success(`New EA secret for ${slot.name || slot.port} — update EASecret in the EA`);
      await navigator.clipboard?.writeText(data.ea_secret).catch(() => {});
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const renameSlot = async (slot, name) => {
    try {
      await api.patch(`/admin/slots/${slot.slot_id}`, { name });
      toast.success("Slot renamed");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const createPod = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/pods", {
        ...form,
        max_users: Number(form.max_users),
        port_start: Number(form.port_start),
      });
      toast.success(`Pod ${form.name} added`);
      setForm({ name: "", max_users: 5, port_start: 7701, region: "" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };

  const patchPod = async (pod, body, msg) => {
    try {
      await api.patch(`/admin/pods/${pod.pod_id}`, body);
      toast.success(msg);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const removePod = async (pod) => {
    try {
      await api.delete(`/admin/pods/${pod.pod_id}`);
      toast.success(`Pod ${pod.name} removed`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const assign = async (userId, body) => {
    try {
      const { data } = await api.post(`/admin/users/${userId}/assign`, body);
      toast.success(`Assigned to ${data.pod} · ${data.slot || data.port}`);
      setPick((p) => ({ ...p, [userId]: "" }));
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const release = async (userId) => {
    try {
      await api.post(`/admin/users/${userId}/release`);
      toast.success("Slot released");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const freeSlots = slots.filter((s) => !s.user_id && s.pod_status !== "offline");

  return (
    <Shell>
      <div className="space-y-5" data-testid="pods-page">
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            className="p-2 text-zinc-500 hover:text-zinc-100 border border-zinc-800 rounded-sm transition-colors duration-200"
            data-testid="back-to-admin"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-2xl font-heading font-semibold tracking-tight">
              Execution Infrastructure
            </h1>
            <p className="text-xs text-zinc-500 font-mono mt-0.5">
              VPS pods, EA slots and slot assignment — admin only
            </p>
          </div>
        </div>

        {cap && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="capacity-cards">
            {[
              ["Pods", cap.pods],
              ["Capacity", cap.capacity],
              ["In use", cap.used],
              ["Free slots", cap.free],
              ["Unassigned", cap.unassigned_traders],
            ].map(([k, v]) => (
              <div key={k} className="border border-zinc-800 bg-zinc-950/60 rounded-sm p-3">
                <div className={label}>{k}</div>
                <div
                  className={`font-mono text-xl mt-1 ${
                    k === "Free slots" && v === 0 ? "text-amber-400" : "text-zinc-100"
                  }`}
                  data-testid={`cap-${k.toLowerCase().replace(/ /g, "-")}`}
                >
                  {v}
                </div>
              </div>
            ))}
          </div>
        )}

        <CollapsibleCard title="Expert Advisor file" storageKey="pods-ea-file"
                         testid="ea-file-card">
          <EaDownload />
        </CollapsibleCard>

        <CollapsibleCard title="EA connections" storageKey="pods-ea-health"
                         testid="ea-health-card">
          <EaHealth />
        </CollapsibleCard>

        <CollapsibleCard title="Add a VPS pod" icon={Plus} storageKey="pods-add"
                         testid="add-pod-card">
          <div className="p-4 sm:p-5">
          <form
            onSubmit={createPod}
            className="grid grid-cols-1 sm:grid-cols-4 gap-3"
            data-testid="add-pod-form"
          >
            <div>
              <Label>Name</Label>
              <input
                required
                data-testid="pod-name-input"
                className={inputCls}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="pod-fra-01"
              />
            </div>
            <div>
              <Label>Region</Label>
              <input
                data-testid="pod-region-input"
                className={inputCls}
                value={form.region}
                onChange={(e) => setForm({ ...form, region: e.target.value })}
                placeholder="eu-central"
              />
            </div>
            <div>
              <Label>Max traders</Label>
              <input
                type="number"
                min="1"
                max="20"
                data-testid="pod-max-users-input"
                className={inputCls}
                value={form.max_users}
                onChange={(e) => setForm({ ...form, max_users: e.target.value })}
              />
            </div>
            <div>
              <Label>First terminal no.</Label>
              <input
                type="number"
                data-testid="pod-port-start-input"
                className={inputCls}
                value={form.port_start}
                onChange={(e) => setForm({ ...form, port_start: e.target.value })}
              />
            </div>
            <div className="sm:col-span-4">
              <button
                type="submit"
                disabled={busy}
                data-testid="create-pod-btn"
                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs uppercase tracking-[0.1em] font-semibold rounded-sm transition-colors duration-200"
              >
                Create pod + slots
              </button>
            </div>
          </form>
          </div>
        </CollapsibleCard>

        {pods.map((pod) => (
          <CollapsibleCard
            key={pod.pod_id}
            title={`${pod.name} · ${pod.region} · ${pod.used}/${pod.max_users} traders`}
            icon={Server}
            storageKey={`pod-${pod.pod_id}`}
            testid={`pod-card-${pod.name}`}
          >
          <div className="p-4 sm:p-5 space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <Server className="h-4 w-4 text-blue-500" />
              <span className="font-heading font-semibold tracking-tight">{pod.name}</span>
              <span className="font-mono text-[11px] text-zinc-500">{pod.region}</span>
              <span
                className={`font-mono text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border rounded-sm ${
                  pod.status === "active"
                    ? "border-emerald-800 text-emerald-400"
                    : pod.status === "draining"
                    ? "border-amber-800 text-amber-400"
                    : "border-red-900 text-red-400"
                }`}
                data-testid={`pod-status-${pod.name}`}
              >
                {pod.status}
              </span>
              <span className="font-mono text-xs text-zinc-400" data-testid={`pod-usage-${pod.name}`}>
                {pod.used}/{pod.max_users} traders
              </span>
              <div className="flex-1" />
              <select
                data-testid={`pod-status-select-${pod.name}`}
                value={pod.status}
                onChange={(e) => patchPod(pod, { status: e.target.value }, "Pod status updated")}
                className="rounded-sm bg-zinc-900/60 border border-zinc-800 px-2 py-1.5 font-mono text-xs text-zinc-100 focus:border-blue-500 focus:outline-none"
              >
                <option value="active">active</option>
                <option value="draining">draining</option>
                <option value="offline">offline</option>
              </select>
              <input
                type="number"
                min="1"
                max="20"
                defaultValue={pod.max_users}
                data-testid={`pod-cap-input-${pod.name}`}
                onBlur={(e) =>
                  Number(e.target.value) !== pod.max_users &&
                  patchPod(pod, { max_users: Number(e.target.value) }, "Capacity updated")
                }
                className="w-16 rounded-sm bg-zinc-900/60 border border-zinc-800 px-2 py-1.5 font-mono text-xs text-zinc-100 focus:border-blue-500 focus:outline-none"
                title="Max traders on this pod"
              />
              <button
                onClick={() => removePod(pod)}
                data-testid={`pod-delete-${pod.name}`}
                className="p-2 text-zinc-600 hover:text-red-400 transition-colors duration-200"
                title="Remove pod (must be empty)"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* mobile: one card per slot */}
            <div className="md:hidden divide-y divide-zinc-800">
              {pod.slots.map((s) => (
                <MobileRow
                  key={s.slot_id}
                  testid={`slot-card-${s.port}`}
                  title={
                    <>
                      <span className="font-mono text-xs text-zinc-100">{s.name}</span>
                      <span className="block font-mono text-[10px] text-zinc-600">
                        {s.slot_id}
                      </span>
                    </>
                  }
                  badge={
                    <EaDot
                      assigned={!!s.user_id}
                      online={!!slots.find((x) => x.slot_id === s.slot_id && x.ea_online)}
                    />
                  }
                  rows={[
                    ["Trader", s.trader ? (
                      <Link to={`/admin/traders/${s.trader.user_id}`} className="text-blue-400">
                        <Masked value={s.trader.email} kind="email"
                                testid={`slot-card-trader-${s.slot_id}`} />
                      </Link>
                    ) : "free"],
                    ["Port", s.port],
                    ["EA endpoint", <span className="text-zinc-400 break-all">{eaEndpoint(s)}</span>],
                    ["EA secret", revealed[s.slot_id] ? s.ea_secret : maskSecret(s.ea_secret)],
                  ]}
                  actions={
                    <>
                      <button
                        data-testid={`slot-card-id-copy-${s.port}`}
                        onClick={() => copyValue(s.slot_id, `slotid-${s.slot_id}`, "Slot ID copied")}
                        className="px-2 py-1 border border-zinc-700 rounded-sm text-[10px] uppercase font-semibold text-zinc-300 hover:text-blue-400 transition-colors duration-200"
                      >
                        Copy slot id
                      </button>
                      <button
                        data-testid={`slot-card-secret-reveal-${s.port}`}
                        onClick={() => setRevealed((r) => ({ ...r, [s.slot_id]: !r[s.slot_id] }))}
                        className="px-2 py-1 border border-zinc-700 rounded-sm text-[10px] uppercase font-semibold text-zinc-300 hover:text-zinc-100 transition-colors duration-200"
                      >
                        {revealed[s.slot_id] ? "Hide secret" : "Reveal secret"}
                      </button>
                      <button
                        data-testid={`slot-card-secret-copy-${s.port}`}
                        onClick={() => copyValue(s.ea_secret, s.slot_id,
                                                 `EA secret for ${s.name} copied`)}
                        className="px-2 py-1 border border-zinc-700 rounded-sm text-[10px] uppercase font-semibold text-zinc-300 hover:text-blue-400 transition-colors duration-200"
                      >
                        Copy secret
                      </button>
                      <button
                        data-testid={`slot-card-secret-rotate-${s.port}`}
                        onClick={() => rotateSecret(s)}
                        className="px-2 py-1 border border-zinc-700 rounded-sm text-[10px] uppercase font-semibold text-zinc-300 hover:text-amber-400 transition-colors duration-200"
                      >
                        Rotate
                      </button>
                    </>
                  }
                  extra={<EaSetup slot={s} />}
                />
              ))}
            </div>

            <div className="overflow-x-auto hidden md:block">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className={th}>Slot name</th>
                    <th className={th}>Slot ID</th>
                    <th className={th}>EA endpoint</th>
                    <th className={th}>EA secret</th>
                    <th className={th}>EA status</th>
                    <th className={th}>Trader</th>
                    <th className={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {pod.slots.map((s) => (
                    <Fragment key={s.slot_id}>
                    <tr data-testid={`slot-row-${s.port}`}>
                      <td className={td}>
                        <input
                          defaultValue={s.name}
                          data-testid={`slot-name-input-${s.port}`}
                          onBlur={(e) =>
                            e.target.value.trim() &&
                            e.target.value !== s.name &&
                            renameSlot(s, e.target.value.trim())
                          }
                          className="w-32 rounded-sm bg-zinc-900/60 border border-zinc-800 px-2 py-1 font-mono text-xs text-zinc-100 focus:border-blue-500 focus:outline-none"
                        />
                      </td>
                      <td className={td}>
                        <div className="flex items-center gap-1.5">
                          <code
                            data-testid={`slot-id-${s.port}`}
                            className="font-mono text-[11px] text-zinc-300 bg-black/50 border border-zinc-900 px-1.5 py-1 rounded-sm"
                          >
                            {s.slot_id}
                          </code>
                          <button
                            title="Copy slot id"
                            data-testid={`slot-id-copy-${s.port}`}
                            onClick={() => copyValue(s.slot_id, `slotid-${s.slot_id}`, "Slot ID copied")}
                            className="p-1 text-zinc-600 hover:text-blue-400 transition-colors duration-200"
                          >
                            {copied === `slotid-${s.slot_id}` ? (
                              <Check className="h-3.5 w-3.5 text-emerald-400" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </button>
                        </div>
                      </td>
                      <td className={`${td} text-zinc-500`}>
                        <div data-testid={`slot-ea-endpoint-${s.port}`}>{eaEndpoint(s)}</div>
                      </td>
                      <td className={td}>
                        <div className="flex items-center gap-1.5">
                          <code
                            data-testid={`slot-secret-${s.port}`}
                            className="font-mono text-[11px] text-zinc-400 bg-black/50 border border-zinc-900 px-1.5 py-1 rounded-sm"
                          >
                            {revealed[s.slot_id] ? s.ea_secret : maskSecret(s.ea_secret)}
                          </code>
                          <button
                            title={revealed[s.slot_id] ? "Hide" : "Reveal"}
                            data-testid={`slot-secret-reveal-${s.port}`}
                            onClick={() =>
                              setRevealed((r) => ({ ...r, [s.slot_id]: !r[s.slot_id] }))
                            }
                            className="p-1 text-zinc-600 hover:text-zinc-200 transition-colors duration-200"
                          >
                            {revealed[s.slot_id] ? (
                              <EyeOff className="h-3.5 w-3.5" />
                            ) : (
                              <Eye className="h-3.5 w-3.5" />
                            )}
                          </button>
                          <button
                            title="Copy EA secret"
                            data-testid={`slot-secret-copy-${s.port}`}
                            onClick={() =>
                              copyValue(s.ea_secret, s.slot_id, `EA secret for ${s.name} copied`)
                            }
                            className="p-1 text-zinc-600 hover:text-blue-400 transition-colors duration-200"
                          >
                            {copied === s.slot_id ? (
                              <Check className="h-3.5 w-3.5 text-emerald-400" />
                            ) : (
                              <Copy className="h-3.5 w-3.5" />
                            )}
                          </button>
                          <button
                            title="Regenerate EA secret (update EASecret in the EA inputs)"
                            data-testid={`slot-secret-rotate-${s.port}`}
                            onClick={() => rotateSecret(s)}
                            className="p-1 text-zinc-600 hover:text-amber-400 transition-colors duration-200"
                          >
                            <RefreshCw className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className={td} data-testid={`slot-ea-status-${s.port}`}>
                        <EaDot
                          assigned={!!s.user_id}
                          online={
                            !!slots.find((x) => x.slot_id === s.slot_id && x.ea_online)
                          }
                        />
                      </td>
                      <td className={td}>
                        {s.trader ? (
                          <Link
                            to={`/admin/traders/${s.trader.user_id}`}
                            className="text-blue-400 hover:text-blue-300"
                          >
                            <Masked value={s.trader.email} kind="email"
                                     testid={`slot-trader-${s.slot_id}`} />
                          </Link>
                        ) : (
                          <span className="text-zinc-600">free</span>
                        )}
                      </td>
                      <td className={td}>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() =>
                              setEaOpen((o) => ({ ...o, [s.slot_id]: !o[s.slot_id] }))
                            }
                            data-testid={`ea-setup-toggle-${s.port}`}
                            className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-blue-400 transition-colors duration-200"
                            title="Show the EA settings for this slot"
                          >
                            {eaOpen[s.slot_id] ? (
                              <ChevronDown className="h-3 w-3" />
                            ) : (
                              <ChevronRight className="h-3 w-3" />
                            )}
                            <Terminal className="h-3 w-3" /> EA setup
                          </button>
                          {s.trader && (
                            <button
                              onClick={() => release(s.trader.user_id)}
                              data-testid={`release-slot-${s.port}`}
                              className="inline-flex items-center gap-1 text-[11px] text-zinc-500 hover:text-red-400 transition-colors duration-200"
                            >
                              <Unplug className="h-3 w-3" /> release
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {eaOpen[s.slot_id] && (
                      <tr data-testid={`ea-setup-row-${s.port}`}>
                        <td colSpan={7} className="px-3 pb-3 border-t border-zinc-900">
                          <EaSetup slot={s} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </CollapsibleCard>
        ))}

        <CollapsibleCard
          title="Trader assignments"
          icon={Cpu}
          count={traders.length}
          storageKey="pods-assignments"
          testid="unassigned-traders"
        >
          <div className="p-4 sm:p-5 space-y-4">
          <p className="text-[11px] text-zinc-600 font-mono">
            Pick a specific free slot for a trader, or let the desk auto-assign the emptiest pod.
          </p>
          <div className="md:hidden divide-y divide-zinc-800">
            {traders.map((t) => {
              const mine = slots.find((s) => s.user_id === t.user_id);
              return (
                <MobileRow
                  key={t.user_id}
                  testid={`assign-card-${t.email}`}
                  title={
                    <Masked value={t.email} kind="email"
                            testid={`assign-card-email-${t.user_id}`}
                            className="text-xs text-zinc-100" />
                  }
                  badge={<EaDot assigned={!!mine} online={!!mine?.ea_online} />}
                  rows={[
                    ["Pod", mine?.pod_name || "—"],
                    ["Slot", mine?.name || "—"],
                  ]}
                  actions={
                    mine ? (
                      <button
                        onClick={() => release(t.user_id)}
                        data-testid={`assign-card-release-${t.email}`}
                        className="px-2 py-1 border border-zinc-700 rounded-sm text-[10px] uppercase font-semibold text-zinc-300 hover:text-red-400 transition-colors duration-200"
                      >
                        Release
                      </button>
                    ) : (
                      <>
                        <select
                          data-testid={`assign-card-slot-select-${t.email}`}
                          value={pick[t.user_id] || ""}
                          onChange={(e) => setPick((p) => ({ ...p, [t.user_id]: e.target.value }))}
                          className="rounded-sm bg-zinc-900/60 border border-zinc-800 px-2 py-1 font-mono text-[11px] text-zinc-100 focus:border-blue-500 focus:outline-none"
                        >
                          <option value="">choose a free slot…</option>
                          {freeSlots.map((s) => (
                            <option key={s.slot_id} value={s.slot_id}>
                              {`${s.pod_name} · ${s.name}`}
                            </option>
                          ))}
                        </select>
                        <button
                          disabled={!pick[t.user_id]}
                          onClick={() => assign(t.user_id, { slot_id: pick[t.user_id] })}
                          data-testid={`assign-card-slot-btn-${t.email}`}
                          className="px-2 py-1 border border-blue-700 text-[10px] uppercase font-semibold text-blue-400 hover:bg-blue-500/10 disabled:opacity-30 rounded-sm transition-colors duration-200"
                        >
                          Assign
                        </button>
                        <button
                          onClick={() => assign(t.user_id, { pod_id: "" })}
                          data-testid={`assign-card-auto-${t.email}`}
                          className="px-2 py-1 border border-zinc-700 rounded-sm text-[10px] uppercase font-semibold text-zinc-300 hover:text-blue-300 transition-colors duration-200"
                        >
                          Auto-assign
                        </button>
                      </>
                    )
                  }
                />
              );
            })}
          </div>

          <div className="overflow-x-auto hidden md:block">
            <table className="w-full">
              <thead>
                <tr>
                  <th className={th}>Trader</th>
                  <th className={th}>Pod</th>
                  <th className={th}>Slot</th>
                  <th className={th}>EA</th>
                  <th className={th}>Assign</th>
                </tr>
              </thead>
              <tbody>
                {traders.map((t) => {
                  const mine = slots.find((s) => s.user_id === t.user_id);
                  return (
                    <tr key={t.user_id} data-testid={`assign-row-${t.email}`}>
                      <td className={td}>
                        <Masked value={t.email} kind="email" testid={`assign-email-${t.user_id}`} />
                      </td>
                      <td className={td}>
                        {mine?.pod_name || <span className="text-zinc-600">—</span>}
                      </td>
                      <td className={td}>
                        {mine?.name || <span className="text-zinc-600">—</span>}
                      </td>
                      <td className={td}>
                        <EaDot assigned={!!mine} online={!!mine?.ea_online} />
                      </td>
                      <td className={td}>
                        {mine ? (
                          <button
                            onClick={() => release(t.user_id)}
                            data-testid={`release-trader-${t.email}`}
                            className="text-[11px] text-zinc-500 hover:text-red-400 transition-colors duration-200"
                          >
                            release
                          </button>
                        ) : (
                          <div className="flex flex-wrap items-center gap-2">
                            <select
                              data-testid={`slot-select-${t.email}`}
                              value={pick[t.user_id] || ""}
                              onChange={(e) =>
                                setPick((p) => ({ ...p, [t.user_id]: e.target.value }))
                              }
                              className="rounded-sm bg-zinc-900/60 border border-zinc-800 px-2 py-1 font-mono text-[11px] text-zinc-100 focus:border-blue-500 focus:outline-none"
                            >
                              <option value="">choose a free slot…</option>
                              {freeSlots.map((s) => (
                                <option
                                  key={s.slot_id}
                                  value={s.slot_id}
                                  label={`${s.pod_name} · ${s.name}`}
                                >
                                  {`${s.pod_name} · ${s.name}`}
                                </option>
                              ))}
                            </select>
                            <button
                              disabled={!pick[t.user_id]}
                              onClick={() =>
                                assign(t.user_id, { slot_id: pick[t.user_id] })
                              }
                              data-testid={`assign-slot-btn-${t.email}`}
                              className="px-2 py-1 border border-blue-700 text-[11px] text-blue-400 hover:bg-blue-500/10 disabled:opacity-30 rounded-sm transition-colors duration-200"
                            >
                              assign
                            </button>
                            <button
                              onClick={() => assign(t.user_id, { pod_id: "" })}
                              data-testid={`assign-trader-${t.email}`}
                              className="text-[11px] text-zinc-500 hover:text-blue-300 transition-colors duration-200"
                            >
                              auto-assign
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </div>
        </CollapsibleCard>
      </div>
    </Shell>
  );
}
